import React from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import winnerLogo from "../assets/icon-white-transparent.png";
import Copyright from "components/Copyright";
import BackButton from "components/BackButton";

const FEATURE_CHECK_PATH = "M3 10l5 5 9-9";

export default function AuthLayout({ badge, tagline, features = [], maxWidth = 400, children }) {
  return (
    <Grid container sx={{ height: "100vh", width: "100%", overflow: "hidden" }}>
      <style>{`
        @keyframes authLayoutGlow { 0%, 100% { opacity: .1; } 50% { opacity: .3; } }
      `}</style>

      <Grid
        item
        sx={{
          display: { xs: "none", md: "flex" },
          flex: "0 0 44%",
          position: "relative",
          overflow: "hidden",
          background: "linear-gradient(155deg, #1e1533 0%, #0f0a1e 52%, #1a0d35 100%)",
          alignItems: "center",
          justifyContent: "center",
          px: 6,
        }}
      >
        <Box
          component="svg"
          viewBox="0 0 480 720"
          preserveAspectRatio="xMidYMid slice"
          fill="none"
          sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none" }}
        >
          <polygon points="240,28 368,98 368,238 240,308 112,238 112,98" stroke="#7c3aed" strokeWidth="1.2" opacity="0.14" />
          <polygon points="240,58 348,120 348,238 240,300 132,238 132,120" stroke="#7c3aed" strokeWidth="0.5" opacity="0.07" />
          <line x1="55" y1="690" x2="425" y2="60" stroke="#a855f7" strokeWidth="1" opacity="0.15" />
          <circle cx="425" cy="60" r="3.5" fill="#a855f7" opacity="0.45" />
          <circle cx="55" cy="690" r="3.5" fill="#7c3aed" opacity="0.35" />
          <polygon points="450,10 510,44 510,112 450,146 390,112 390,44" stroke="#a855f7" strokeWidth="0.9" opacity="0.1" />
        </Box>
        <Box sx={{ position: "absolute", top: 68, right: 44, animation: "authLayoutGlow 4s ease-in-out infinite" }}>
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
            <polygon points="9,1 16.5,5 16.5,13 9,17 1.5,13 1.5,5" stroke="#c084fc" strokeWidth="1.4" />
          </svg>
        </Box>
        <Box sx={{ position: "absolute", bottom: 148, left: 30, animation: "authLayoutGlow 6s ease-in-out infinite 1.5s" }}>
          <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
            <polygon points="5.5,1 10,3.5 10,7.5 5.5,10 1,7.5 1,3.5" stroke="#9d8bc4" strokeWidth="1.1" />
          </svg>
        </Box>

        <Box sx={{ position: "relative", zIndex: 2, textAlign: "center", width: "100%" }}>
          {badge && (
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                background: "rgba(245,158,11,.12)",
                border: "1px solid rgba(245,158,11,.4)",
                borderRadius: "100px",
                padding: "6px 14px",
                mb: "22px",
              }}
            >
              {badge.icon}
              <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: ".12em", color: "#f59e0b", textTransform: "uppercase" }}>
                {badge.label}
              </Typography>
            </Box>
          )}
          <Box
            sx={{
              mx: "auto",
              mb: "26px",
              width: 96,
              height: 96,
              borderRadius: "50%",
              background: "rgba(124,58,237,.18)",
              border: "1px solid rgba(168,85,247,.35)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box
              component="img"
              src={winnerLogo}
              alt="Winner Express"
              sx={{ width: 64, height: 64, objectFit: "contain" }}
            />
          </Box>
          <Typography sx={{ fontFamily: "'Space Grotesk', 'Noto Sans Lao', sans-serif", fontSize: 26, fontWeight: 700, color: "#ffffff", letterSpacing: "-.01em", mb: "6px" }}>
            Winner Express
          </Typography>
          <Typography sx={{ fontSize: 10, fontWeight: 700, letterSpacing: ".22em", color: "#5a4878", textTransform: "uppercase", mb: "44px" }}>
            {tagline}
          </Typography>
          {features.length > 0 && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", alignItems: "flex-start", width: "fit-content", mx: "auto" }}>
              {features.map((label) => (
                <Box
                  key={label}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    background: "rgba(124,58,237,.1)",
                    border: "1px solid rgba(168,85,247,.2)",
                    borderRadius: "100px",
                    padding: "8px 16px 8px 9px",
                  }}
                >
                  <Box sx={{ width: 24, height: 24, borderRadius: "50%", background: "rgba(124,58,237,.22)", display: "flex", alignItems: "center", justifyContent: "center", flex: "none" }}>
                    <svg width="10" height="10" viewBox="0 0 20 20" fill="none">
                      <path d={FEATURE_CHECK_PATH} stroke="#c084fc" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </Box>
                  <Typography sx={{ fontSize: 13, color: "#c4b5fd", fontWeight: 500 }}>{label}</Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Grid>

      <Grid
        item
        sx={{
          flex: 1,
          minWidth: 0,
          background: "#ffffff",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          px: { xs: 3, sm: 5 },
          py: 6,
          overflowY: "auto",
        }}
      >
        <BackButton sx={{ top: 28, left: 28, border: "1.5px solid #ede9f6" }} />

        <Box sx={{ width: "100%", maxWidth }}>
          {children}
          <Copyright sx={{ mt: "36px", pt: "22px", borderTop: "1px solid #f3f0fa" }} />
        </Box>
      </Grid>
    </Grid>
  );
}

export const authInputSx = (theme) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    backgroundColor: "#faf9ff",
    "& fieldset": { borderColor: "#e5dff5", borderWidth: "1.5px" },
    "&:hover fieldset": { borderColor: "#e5dff5" },
    "&.Mui-focused fieldset": { borderColor: theme.palette.secondary.main },
  },
  "& .MuiOutlinedInput-input": {
    padding: "14px 14px 14px 8px",
    "&:-webkit-autofill": {
      WebkitBoxShadow: "0 0 0 100px #faf9ff inset",
      WebkitTextFillColor: "#1b1728",
      borderRadius: "12px",
    },
  },
});

export const authFieldLabelSx = (theme) => ({
  display: "block",
  fontSize: 11,
  fontWeight: 800,
  letterSpacing: ".1em",
  color: theme.palette.secondary.dark,
  textTransform: "uppercase",
  mb: "7px",
});

export const authButtonSx = (theme) => ({
  height: 52,
  borderRadius: "14px",
  background: `linear-gradient(135deg, ${theme.palette.secondary.light} 0%, ${theme.palette.secondary.dark} 100%)`,
  boxShadow: "0 8px 24px rgba(92,33,182,.3)",
  fontSize: 15,
  fontWeight: 700,
  letterSpacing: ".05em",
  "&:hover": {
    background: `linear-gradient(135deg, ${theme.palette.secondary.light} 0%, ${theme.palette.secondary.dark} 100%)`,
    boxShadow: "0 12px 32px rgba(92,33,182,.42)",
    transform: "translateY(-1px)",
  },
  "&.Mui-disabled": {
    background: "#e5dff5",
    color: "#b8aece",
  },
});
